import { z } from 'zod'

/** Roles and areas (ARCHITECTURE §6). */
export const PLATFORM_ROLES = ['platform_admin', 'platform_operator'] as const
export type PlatformRole = (typeof PLATFORM_ROLES)[number]

export const STAFF_ROLES = ['manager', 'reviewer'] as const
export type StaffRole = (typeof STAFF_ROLES)[number]

export const INVESTOR_ROLES = ['investor', 'investor_admin'] as const
export type InvestorRole = (typeof INVESTOR_ROLES)[number]

export const MEMBERSHIP_ROLES = [...STAFF_ROLES, ...INVESTOR_ROLES] as const
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number]

export const ORG_KINDS = ['fruuit', 'investor'] as const
export type OrgKind = (typeof ORG_KINDS)[number]

export const ORG_STATUSES = ['active', 'suspended'] as const
export type OrgStatus = (typeof ORG_STATUSES)[number]

export const USER_STATUSES = ['invited', 'active', 'deactivated'] as const
export type UserStatus = (typeof USER_STATUSES)[number]

export const MEMBERSHIP_STATUSES = ['active', 'revoked'] as const
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number]

export const IDENTITY_PROVIDERS = ['google', 'microsoft', 'email'] as const
export type IdentityProvider = (typeof IDENTITY_PROVIDERS)[number]

/** Which membership roles are valid in which org kind. */
export const ROLES_BY_ORG_KIND: Readonly<Record<OrgKind, readonly MembershipRole[]>> = {
  fruuit: STAFF_ROLES,
  investor: INVESTOR_ROLES,
}

export const AREAS = ['app', 'workspace', 'admin'] as const
export type Area = (typeof AREAS)[number]

export const membershipSchema = z.object({
  orgId: z.string(),
  orgKind: z.enum(ORG_KINDS),
  role: z.enum(MEMBERSHIP_ROLES),
})
export type Membership = z.infer<typeof membershipSchema>

/**
 * The authenticated caller, resolved by the session middleware on every
 * request from the users + memberships tables (only active memberships of
 * active orgs are included). Never built from request input.
 */
export const principalSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string(),
  platformRole: z.enum(PLATFORM_ROLES).nullable(),
  memberships: z.array(membershipSchema),
  sessionId: z.string(),
  impersonatedBy: z.string().nullable(),
  mfaVerifiedAt: z.string().nullable(),
})
export type Principal = z.infer<typeof principalSchema>

export function isPlatformAdmin(principal: Principal): boolean {
  return (
    principal.platformRole === 'platform_admin' || principal.platformRole === 'platform_operator'
  )
}

export function isPlatformOperator(principal: Principal): boolean {
  return principal.platformRole === 'platform_operator'
}

export function staffRolesOf(principal: Principal): StaffRole[] {
  const roles = new Set<StaffRole>()
  for (const m of principal.memberships) {
    if (m.orgKind === 'fruuit' && (m.role === 'manager' || m.role === 'reviewer')) roles.add(m.role)
  }
  return [...roles]
}

export function hasStaffRole(principal: Principal, role?: StaffRole): boolean {
  const roles = staffRolesOf(principal)
  return role === undefined ? roles.length > 0 : roles.includes(role)
}

/** The caller's role in one investor org, preferring investor_admin. */
export function investorRoleIn(principal: Principal, orgId: string): InvestorRole | null {
  let role: InvestorRole | null = null
  for (const m of principal.memberships) {
    if (m.orgKind !== 'investor' || m.orgId !== orgId) continue
    if (m.role === 'investor_admin') return 'investor_admin'
    if (m.role === 'investor') role = 'investor'
  }
  return role
}

/** Investor org ids the caller belongs to, sorted for a stable default. */
export function investorOrgIds(principal: Principal): string[] {
  const ids = new Set<string>()
  for (const m of principal.memberships) if (m.orgKind === 'investor') ids.add(m.orgId)
  return [...ids].sort()
}

export function fruuitOrgId(principal: Principal): string | null {
  return principal.memberships.find((m) => m.orgKind === 'fruuit')?.orgId ?? null
}

/** SPA areas the caller may open, in routing priority order. */
export function areasFor(principal: Principal): Area[] {
  const areas: Area[] = []
  if (investorOrgIds(principal).length > 0) areas.push('app')
  if (hasStaffRole(principal)) areas.push('workspace')
  if (isPlatformAdmin(principal)) areas.push('admin')
  return areas
}
